/**
 * CLE-2811 — the wizard while a contract model is still generating.
 *
 * The page shows the same layout during generation as after it (step rail, wizard, conversation), and
 * fills it as the worker reads: the plans it finds (Lines of Business) and each plan's contract terms
 * (Contract Terms), from the progress's optional `partial`. These are PREVIEWS — read-only, not saved —
 * and the real, editable recommendations take their place when the pass finishes.
 *
 * Pure helpers, so the merging rules are tested without a DOM. Everything is null-safe: with no
 * `partial` (an older worker, or nothing read yet) the steps keep their skeletons.
 */

import {
  IWorkerPartialFacts,
  IWorkerPartialPlan,
  IWorkerPartialTerm,
  IWorkerProgress,
} from "../../../store/queries/contractModelDrafts";
import {
  PLANS_FINDING,
  TERMS_READING,
  allPlansRead,
  extractingPlansLine,
  plansFoundLine,
} from "../copy";
import { FIELD_SPECS, IFieldSpec, fieldLabel } from "./fieldSpecs";
import { StepId } from "./steps";

export type PreviewStatus = "pending" | "extracting" | "done" | "failed";

const PLAN_STATUSES: PreviewStatus[] = [
  "pending",
  "extracting",
  "done",
  "failed",
];

export interface IPlanPreview {
  /** Stable across polls: the payer id when there is one, else the name. */
  key: string;
  name: string;
  payerId: number | null;
  status: PreviewStatus;
}

export interface IPreviewField {
  label: string;
  value: string;
}

export interface ITermPreview {
  key: string;
  name: string;
  payerId: number | null;
  /** `pending` for a plan whose terms the worker has not started. */
  status: PreviewStatus;
  /** What was read — only once `done`. */
  fields: IPreviewField[];
  openQuestions: string[];
}

export interface IPartialPreview {
  contractType: string | null;
  /** The contract-wide facts read so far, as label/value rows. */
  facts: IPreviewField[];
  plans: IPlanPreview[];
  terms: ITermPreview[];
}

// ---------------------------------------------------------------------------------------------
// Readers
// ---------------------------------------------------------------------------------------------

const text = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;

const payerIdOf = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const keyOf = (payerId: number | null, name: string): string =>
  payerId != null ? `p${payerId}` : `n:${name.toLowerCase()}`;

const getPath = (obj: Record<string, unknown>, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (cur, part) =>
        cur && typeof cur === "object"
          ? (cur as Record<string, unknown>)[part]
          : undefined,
      obj
    );

/** Kinds that are not facts about the contract: the scope ids and the agent's note. */
const SKIPPED_KINDS: IFieldSpec["kind"][] = [
  "entity",
  "payerSelect",
  "readonly",
];

const formatValue = (spec: IFieldSpec, v: unknown): string | null => {
  if (v == null || v === "") return null;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return null;
    if (spec.kind === "percent") return `${v}%`;
    if (spec.kind === "fraction") return `${Math.round(v * 1000) / 10}%`;
    if (spec.kind === "money") return `$${(v / 100).toLocaleString()}`;
    return String(v);
  }
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return null;
    return spec.options?.find((o) => o.value === t)?.label ?? t;
  }
  return null;
};

/**
 * A contract term's modeling as label/value rows, in the Contract Terms form's own order and words
 * (`FIELD_SPECS.contract_term`), empty values left out. Read-only, so every branch's fields show when
 * they carry a value.
 */
export const termFields = (
  modeling: Record<string, unknown> | null | undefined
): IPreviewField[] => {
  if (!modeling || typeof modeling !== "object") return [];
  const out: IPreviewField[] = [];
  FIELD_SPECS.contract_term.forEach((spec) => {
    if (SKIPPED_KINDS.includes(spec.kind)) return;
    const value = formatValue(spec, getPath(modeling, spec.key));
    if (value) out.push({ label: fieldLabel(spec, modeling), value });
  });
  return out;
};

/** The contract-wide facts as rows. An escalator given as a sentence reads as the increase basis. */
export const factsFields = (
  facts: IWorkerPartialFacts | null | undefined
): IPreviewField[] => {
  if (!facts || typeof facts !== "object") return [];
  const escalator =
    typeof facts.escalator === "string"
      ? { basis: facts.escalator }
      : facts.escalator;
  return termFields({
    signedDate: facts.signedDate,
    effectiveDate: facts.effectiveDate,
    timelyFilingDays: facts.timelyFilingDays,
    escalator,
    implantTerms: facts.implantTerms,
    multipleProcedureMethodology: facts.multipleProcedureMethodology,
  });
};

const planFrom = (p: IWorkerPartialPlan): IPlanPreview | null => {
  const name = text(p?.name);
  if (!name) return null;
  const payerId = payerIdOf(p.payerId);
  return {
    key: keyOf(payerId, name),
    name,
    payerId,
    status: PLAN_STATUSES.includes(p.status) ? p.status : "pending",
  };
};

const sameTerm =
  (plan: { payerId: number | null; name: string }) => (t: IWorkerPartialTerm) =>
    plan.payerId != null && payerIdOf(t.payerId) != null
      ? payerIdOf(t.payerId) === plan.payerId
      : (text(t.name) ?? "").toLowerCase() === plan.name.toLowerCase();

const termFrom = (
  plan: { key: string; name: string; payerId: number | null },
  t: IWorkerPartialTerm | undefined
): ITermPreview => {
  const status: PreviewStatus =
    t && PLAN_STATUSES.includes(t.status) ? t.status : "pending";
  return {
    key: plan.key,
    name: plan.name,
    payerId: plan.payerId,
    status,
    fields: status === "done" ? termFields(t?.proposedModeling) : [],
    openQuestions:
      status === "done" && Array.isArray(t?.openQuestions)
        ? (t?.openQuestions ?? []).filter(
            (q): q is string => typeof q === "string" && q.trim() !== ""
          )
        : [],
  };
};

// ---------------------------------------------------------------------------------------------
// The merge
// ---------------------------------------------------------------------------------------------

/**
 * CLE-2811 (2026-09-28) — how a plan or term with a payer id is named on screen: the page passes its payer
 * resolver (`payerNames.ts` — the run's and the slim poll's `payerNames`, the payers list, then the worker's
 * own name, never the id). Plans without an id keep the worker's name.
 */
export type PlanNameResolver = (payerId: number, workerName: string) => string;

/**
 * The preview of what has been read so far, or null when there is nothing (no `partial`, or an empty
 * one). Plans keep the worker's discovery order and cards never move as they complete, so each lands
 * where its real recommendation will; a term whose plan was not listed (a worker that reports terms
 * only) is appended in the terms' order. Duplicates — the same payer id, else the same name — merge.
 *
 * Matching runs on the worker's own names; `nameOf` renames what is SHOWN, after the merge, so a plan
 * with an id and its term without one still pair up by the name the worker gave both.
 */
export const partialPreviewOf = (
  progress: IWorkerProgress | null,
  nameOf?: PlanNameResolver
): IPartialPreview | null => {
  const partial = progress?.partial;
  if (!partial || typeof partial !== "object") return null;

  const plans: IPlanPreview[] = [];
  (Array.isArray(partial.plans) ? partial.plans : []).forEach((p) => {
    const plan = planFrom(p);
    if (plan && !plans.some((x) => x.key === plan.key)) plans.push(plan);
  });

  const rawTerms = (Array.isArray(partial.terms) ? partial.terms : []).filter(
    (t) => t && text(t.name)
  );
  const terms = plans.map((plan) =>
    termFrom(plan, rawTerms.find(sameTerm(plan)))
  );
  rawTerms.forEach((t) => {
    const name = text(t.name) as string;
    const payerId = payerIdOf(t.payerId);
    const key = keyOf(payerId, name);
    if (terms.some((x) => x.key === key || sameTerm(x)(t))) return;
    terms.push(termFrom({ key, name, payerId }, t));
  });

  const facts = factsFields(partial.facts ?? null);
  const contractType = text(partial.contractType);
  if (!plans.length && !terms.length && !facts.length && !contractType)
    return null;
  const shown = <T extends { payerId: number | null; name: string }>(
    item: T
  ): T =>
    nameOf && item.payerId != null
      ? { ...item, name: nameOf(item.payerId, item.name) }
      : item;
  return {
    contractType,
    facts,
    plans: plans.map(shown),
    terms: terms.map(shown),
  };
};

// ---------------------------------------------------------------------------------------------
// The one-line explanations
// ---------------------------------------------------------------------------------------------

export interface IStatusCounts {
  total: number;
  done: number;
  failed: number;
  /** Names of the ones being read now. */
  inProgress: string[];
}

export const statusCounts = (
  items: { name: string; status: PreviewStatus }[]
): IStatusCounts => ({
  total: items.length,
  done: items.filter((i) => i.status === "done").length,
  failed: items.filter((i) => i.status === "failed").length,
  inProgress: items.filter((i) => i.status === "extracting").map((i) => i.name),
});

/**
 * "Finding the plans in this contract…" → "Extracting Medflex, SuperMed… 2 of 4 done" → "All 4 plans
 * read". Built from the counts only — it never says more than the worker reported.
 */
export const progressLine = (
  items: { name: string; status: PreviewStatus }[],
  empty: string
): string => {
  const c = statusCounts(items);
  if (c.total === 0) return empty;
  if (c.done === c.total) return allPlansRead(c.total);
  if (c.inProgress.length)
    return extractingPlansLine(c.inProgress, c.done, c.total, c.failed);
  return plansFoundLine(c.done, c.total, c.failed);
};

export const plansLine = (preview: IPartialPreview | null): string =>
  progressLine(preview?.plans ?? [], PLANS_FINDING);

export const termsLine = (preview: IPartialPreview | null): string =>
  progressLine(preview?.terms ?? [], TERMS_READING);

// ---------------------------------------------------------------------------------------------
// The rail while generating
// ---------------------------------------------------------------------------------------------

/** Steps that are views, not things the pass builds. */
const NOT_BUILT: StepId[] = ["documents", "changes", "build"];

/**
 * The rail steps to mark as still being built: while a FIRST generation runs (no recommendations yet),
 * every visible content step. A regeneration keeps the previous version on screen, so nothing is marked
 * — the progress strip says a pass is running.
 */
export const buildingSteps = (
  visible: StepId[],
  recommendationCount: number,
  generating: boolean
): StepId[] =>
  generating && recommendationCount === 0
    ? visible.filter((s) => !NOT_BUILT.includes(s))
    : [];

/**
 * Whether Lines of Business belongs on the rail during a first generation. It is decided by the
 * recommendations once they exist; before that, it shows while the plans are unknown or more than one,
 * and goes once the worker has found exactly one.
 */
export const showSegmentsWhileGenerating = (
  preview: IPartialPreview | null
): boolean => (preview?.plans.length ?? 0) !== 1;
